import {ChangeDetectionStrategy, Component} from "@angular/core";
import { ConfigSection } from '../config-section';
import {EmailSettings, MsOAuthEmailSettings, MsOAuthEmailSettingType, SmtpEmailSettings, SmtpEmailSettingType} from "@admin/models/email-settings-config";
import {BehaviorSubject, map,  Observable, of} from "rxjs";
import { UntypedFormBuilder } from "@angular/forms";
import {EmailAndTestEmailService} from "@admin/services/email-and-test-email-http.service";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: 'mojo-send-test-email',
  templateUrl: './mojo-send-test-email.component.html',
  changeDetection: ChangeDetectionStrategy.Default
})
export class MojoSendTestEmailComponent extends ConfigSection<EmailSettings> {

  private emailConfig : EmailSettings;
  private sendInProgress : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private readonly formBuilder: UntypedFormBuilder,
              private readonly emailService: EmailAndTestEmailService,
              private readonly messageService: ToastrService) {
    super();
  }

  /**
   * Gets a value indicating whether this component is currently displaying SMTP configuration.
   */
  isSmtp() { return this.emailConfig.type === SmtpEmailSettingType; }

  /**
   * Gets a value indicating whether this component is currently displaying MS Exchange OAuth 2.0 configuration.
   */
  isMsOauth() { return this.emailConfig.type === MsOAuthEmailSettingType; }

  /**
   * Gets a value indicating whether we may not send a test email.
   *
   * This is `true` if we didn't enter a recipient email address or
   * if a test email sending is already in-progress.
   */
  cannotSendTestEmail() : Observable<boolean> {
    if(!this.form.controls['testEmailRecipient'].value) return of(true);
    return this.sendInProgress;
  }

  protected initialiseConfig(): Observable<EmailSettings> {
    return this.emailService.getEmailConfig();
  }

  protected createForm(config: EmailSettings): void {
    this.emailConfig = config;
    const formData = this.getFormData(config);
    this.form = this.formBuilder.group({...formData, testEmailRecipient: ''});
  }

  private getFormData(config: EmailSettings) : object {
    switch(config.type)
    {
    case SmtpEmailSettingType:
      const smtpConf = <SmtpEmailSettings> config;
      return {
        host: smtpConf.host,
        port: smtpConf.port,
        username: smtpConf.username,
      };
    case MsOAuthEmailSettingType:
      const oauthConf = <MsOAuthEmailSettings> config;
      return {
        tenantId: oauthConf.tenantId,
        clientId: oauthConf.clientId,
      };
    }
  }

  /**
   * Hard-coded to return an observable of true; this 'settings' section doesn't really have any
   * editable settings, thus there is nothing to be saved.
   */
  submit(): Observable<boolean> { return of(true); }

  /**
   * Begins the sending of the test email.
   *
   * This occurs semi-asynchronously.  Some back-end validation is performed semi-synchronously,
   * so that if the email sending settings are obviously incorrect then we can get immediate
   * error feedback.
   * These are the likes of errors such as a blank password, or SMTP host not found which result in
   * not being able to send email at all.  For these, we can display near-immediate feedback to the
   * user telling them that something is wrong.
   *
   * For other errors, the problem cannot be reported back to the user immediately, because we might
   * never know that there was a problem.  The only sign that something is wrong is that the recipient
   * never receives the email.  In these cases
   */
  async sendEmail() : Promise<void> {
    if(this.sendInProgress.value) return Promise.resolve();
    this.sendInProgress.next(true);
    const uniqueData = crypto.randomUUID();
    const address : string = this.form.controls['testEmailRecipient'].value;

    console.log('Sending test email with data ', uniqueData, ' to ', address);
    const result = await this.emailService.sendTestEmail({ address, uniqueData });
    if(result === null)
      this.messageService.info(`A test email has been sent to '${address}' containing the data '${uniqueData}'.`, 'Email sent', {timeOut: 0, extendedTimeOut: 0});
    else
      this.messageService.error(`A test email could not be sent to '${address}': ${result}.`, 'Email error', {timeOut: 0, extendedTimeOut: 0});
    this.sendInProgress.next(false);
  }
}
